import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  TableScroll,
} from "@playbookai/ui";
import { isOnboardingTourId, type OnboardingTourId } from "@playbookai/types";
import { resetTours } from "../../api/onboarding-api.js";
import definitions from "../../onboarding/tour-definitions.json";

export function OnboardingToursAdmin() {
  const [selected, setSelected] = useState<OnboardingTourId | "all" | null>(
    null,
  );
  const [notice, setNotice] = useState("");
  const reset = useMutation({
    mutationFn: resetTours,
    onSuccess: (_result, id) => {
      setNotice(
        `${id === "all" ? "All tours" : definitions.find((tour) => tour.id === id)?.label + " tour"} reset. Users will see them on their next relevant visit.`,
      );
      setSelected(null);
    },
  });
  const label =
    selected === "all"
      ? "all tours"
      : `${definitions.find((tour) => tour.id === selected)?.label ?? ""} tour`;
  function open(id: OnboardingTourId | "all") {
    reset.reset();
    setNotice("");
    setSelected(id);
  }
  return (
    <Card>
      <CardHeader>
        <div>
          <h2 className="text-base font-semibold text-pb-text">
            Onboarding tours
          </h2>
          <p className="mt-1 text-xs text-pb-text-muted">
            Offer tours again to everyone in this workspace.
          </p>
        </div>
        <Button variant="secondary" onClick={() => open("all")}>
          Reset all tours for all users
        </Button>
      </CardHeader>
      <CardBody>
        {notice ? (
          <p role="status" className="mb-3 text-sm text-pb-text">
            {notice}
          </p>
        ) : null}
        <TableScroll aria-label="Onboarding tours">
          {/* eslint-disable-next-line ds/no-hand-built -- DataTable would restyle this header (uppercase 10px labels on the elevated fill) and cell padding; no PR 3 look row approves that, so the raw table stays inside TableScroll. */}
          <table className="w-full text-left text-body-small">
            <caption className="sr-only">Onboarding tours</caption>
            <thead>
              <tr className="border-b border-pb-border">
                <th className="px-3 py-3">Tour</th>
                <th className="px-3 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {definitions.map((tour) => (
                <tr
                  key={tour.id}
                  className="border-b border-pb-border transition-colors hover:pb-overlay-paint last:border-0"
                >
                  <th
                    scope="row"
                    className="whitespace-nowrap px-3 py-3 font-medium"
                  >
                    {tour.label}
                  </th>
                  <td className="px-3 py-3 text-right">
                    <Button
                      variant="secondary"
                      aria-label={`Reset ${tour.label} tour for all users`}
                      onClick={() => {
                        if (isOnboardingTourId(tour.id)) open(tour.id);
                      }}
                    >
                      Reset for all users
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      </CardBody>
      {/* ConfirmDialog (d2): nothing is lost, so the neutral tone. */}
      <ConfirmDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        onConfirm={() => {
          if (selected) reset.mutate(selected);
        }}
        title={`Reset ${label} for all users?`}
        tone="neutral"
        confirmLabel={reset.isPending ? "Resetting…" : "Reset tours"}
        confirmDisabled={reset.isPending}
        cancelDisabled={reset.isPending}
      >
        <p>
          Everyone in this workspace will see{" "}
          {selected === "all" ? "these tours" : "this tour"} again on their next
          relevant visit or collaboration event. Completed and skipped tours are
          included. Active tours won’t be interrupted.
        </p>
        {reset.error ? (
          <p role="alert" className="mt-3 text-sm text-pb-red-text">
            {reset.error.message}
          </p>
        ) : null}
      </ConfirmDialog>
    </Card>
  );
}
